'use client';

import { Box } from '@terra/ui-styled-system';
import type { ParallelAgentStatus } from './parallel-agent-batch';

const GRID_CELLS = [0, 1, 2, 3, 4, 5, 6, 7, 8] as const;
const CENTER_CELL = 4;
const CLOCKWISE_RING = [0, 1, 2, 5, 8, 7, 6, 3];
const RING_STEP_MS = 100;

function ringDelayMs(cell: number): number {
  return Math.max(CLOCKWISE_RING.indexOf(cell), 0) * RING_STEP_MS;
}

export function AgentStatusDots({
  status,
  label,
}: {
  status: ParallelAgentStatus;
  label?: string;
}) {
  const isRunning = status === 'running';
  const isWaiting = status === 'waiting';

  return (
    <Box
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-testid="agent-status-dots"
      data-status={status}
      display="grid"
      gridTemplateColumns="repeat(3, 3px)"
      gridAutoRows="3px"
      gap="2px"
      flexShrink={0}
      color={
        status === 'error'
          ? 'fg.error'
          : status === 'completed'
            ? 'fg.default'
            : status === 'aborted'
              ? 'fg.subtle'
              : 'fg.muted'
      }
      transition="color 0.3s ease"
    >
      {GRID_CELLS.map((cell) => (
        <Box
          key={cell}
          borderRadius="full"
          bg="currentColor"
          opacity={
            isRunning && cell === CENTER_CELL
              ? 0.25
              : status === 'aborted'
                ? 0.6
                : 1
          }
          transition="opacity 0.3s ease"
          animation={
            isRunning && cell !== CENTER_CELL
              ? 'pulse 0.8s ease-in-out infinite'
              : isWaiting
                ? 'pulse 1.6s ease-in-out infinite'
                : undefined
          }
          _motionReduce={{ animation: 'none', opacity: 1 }}
          style={
            isRunning ? { animationDelay: `${ringDelayMs(cell)}ms` } : undefined
          }
        />
      ))}
    </Box>
  );
}
